import type { AuthProvider } from '@refinedev/core';
import type { LoginCredentials, LoginResponse, AuthUser } from '@/types';
import axios from 'axios';
import apiClient, { tokenStorage, impersonationStorage } from '@/shared/apiClient';
import { clearUser, isImpersonationValid, isSuperAdmin, loadUser, parseJwt, storeUser, syncSessionFromAccessToken } from '@/auth/session';

// ── Stilles Refresh via HttpOnly-Cookie (SRV-B2) ─────

function _getCsrfToken(): string {
  const match = document.cookie.split('; ').find((row) => row.startsWith('sr_csrf='));
  return match ? (match.split('=')[1] ?? '') : '';
}

async function _trySilentRefresh(): Promise<string | null> {
  try {
    const resp = await axios.post<{ access_token: string; token_type: string }>('/api/v1/auth/refresh', {}, { withCredentials: true, headers: { 'X-CSRF-Token': _getCsrfToken() } });
    return resp.data.access_token;
  } catch {
    return null;
  }
}

// ── Refine AuthProvider ──────────────────────────────

export const authProvider: AuthProvider = {
  login: async ({ email, password }: LoginCredentials) => {
    try {
      const resp = await apiClient.post<LoginResponse>('/auth/login', {
        email,
        password,
      });
      const data = resp.data;

      if (data.requires_2fa) {
        // 2FA-Flow: Weiterleitung auf 2FA-Seite mit Temp-Token
        return {
          success: false,
          error: {
            name: '2FA erforderlich',
            message: 'Bitte 2FA-Code eingeben.',
          },
          redirectTo: '/login/2fa',
        };
      }

      // SRV-B2: Nur Access-Token in Memory, Refresh-Token ist HttpOnly-Cookie
      if (data.access_token) {
        tokenStorage.setAccess(data.access_token);
      }
      const user: AuthUser = {
        id: data.user_id,
        email,
        role: data.role,
        tenantId: data.tenant_id,
        requires2fa: data.requires_2fa,
      };
      storeUser(user);

      // Routing nach Rolle
      const redirectTo = data.role === 'super_admin' ? '/admin' : '/dashboard';
      return { success: true, redirectTo };
    } catch (error: unknown) {
      let message = 'Login fehlgeschlagen.';
      if (typeof error === 'object' && error !== null && 'response' in error && typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail === 'string') {
        message = (error as { response: { data: { detail: string } } }).response.data.detail;
      }
      return {
        success: false,
        error: { name: 'Login-Fehler', message },
      };
    }
  },

  logout: async () => {
    const token = tokenStorage.getAccess();
    if (token) {
      try {
        await apiClient.post('/auth/logout', { token });
      } catch {
        // Immer ausloggen, auch bei Netzwerkfehler
      }
    }
    tokenStorage.clear();
    clearUser();
    impersonationStorage.clear();
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    let token = tokenStorage.getAccess();

    // SRV-B2: Kein Access-Token im Speicher (z.B. nach Page-Reload)?
    if (!token) {
      // 1. Impersonation-Rehydration (PLAN.md „Impersonation-Refresh"):
      //    Vor dem Cookie-Refresh prüfen, ob im sessionStorage noch ein gültiger
      //    Impersonation-Access-Token liegt. Wenn ja, diesen wiederherstellen,
      //    damit der Admin-Refresh-Cookie die Impersonation nicht überschreibt.
      const original = impersonationStorage.peek();
      if (original && isImpersonationValid(original)) {
        tokenStorage.setAccess(original.impersonatedAccess);
        storeUser(original.impersonatedUser);
        token = original.impersonatedAccess;
      } else {
        // Abgelaufene oder fehlende Impersonation aufräumen.
        if (original) impersonationStorage.clear();
        // 2. Stilles Refresh via HttpOnly-Cookie (Admin-Session).
        token = await _trySilentRefresh();
        if (token) {
          tokenStorage.setAccess(token);
          syncSessionFromAccessToken(token);
        }
      }
    }

    if (!token) return { authenticated: false, redirectTo: '/login' };

    const payload = parseJwt(token);
    if (!payload) return { authenticated: false, redirectTo: '/login' };

    // Token abgelaufen → aktive Impersonation beenden, dann stilles Refresh via Cookie.
    if (payload.exp * 1000 < Date.now()) {
      if (impersonationStorage.isActive()) {
        impersonationStorage.clear();
      }
      const newToken = await _trySilentRefresh();
      if (!newToken) {
        tokenStorage.clear();
        clearUser();
        return { authenticated: false, redirectTo: '/login' };
      }
      tokenStorage.setAccess(newToken);
      syncSessionFromAccessToken(newToken);
    }

    return { authenticated: true };
  },

  getIdentity: async () => {
    return loadUser();
  },

  getPermissions: async () => {
    const user = loadUser();
    return user?.role ?? null;
  },

  onError: async (error: unknown) => {
    if (typeof error === 'object' && error !== null && 'status' in error && (error as { status: number }).status === 401) {
      return { logout: true, redirectTo: '/login' };
    }
    return { error: error as Error };
  },
};

// ── Hilfsfunktion für Rolle ──────────────────────────

export function getCurrentUser(): AuthUser | null {
  return loadUser();
}
export { isSuperAdmin };
